/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType, PortalType } from '../../config/enum';
import { detectPortalType } from '../../contexts/PortalContext/PortalProvider';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  SessionAPIResponse,
  SessionInitialProperties,
  CommercialSessionInterface,
} from '../../interface/charging-session.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import paymentApi from '../../utils/api/payments';

import { setNotificationMessage } from './NotificationManagementSlice';

interface FetchParameters {
  search?: string;
  page?: number;
  limit?: number;
}

const portalType = detectPortalType();

/**
 * A thunk to fetch sessions specifically for device details view.
 * @param {FetchParameters} data - The query parameters for the api call.
 * @param {any} thunkAPI - The Redux Toolkit thunk API object.
 * @returns {Promise<SessionAPIResponse<CommercialSessionInterface>>} The API response.
 */
export const fetchDeviceDetailsChargingSessions = createAsyncThunk<
  SessionAPIResponse<CommercialSessionInterface>,
  FetchParameters
>('device-details-charging-session/fetchDeviceDetailsChargingSessions', async (data: FetchParameters, { dispatch }) => {
  try {
    const response = await paymentApi.fetchChargingSessions(removeEmptyKeys(data));

    return {
      ...response.data,
      results: response.data.results.map((roleData: any) => ({
        ...roleData,
        date: ConvertUTCtoDate(roleData.createdAt),
        id: roleData._id,
      })),
    };
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message,
        code: error?.response?.data?.code,
        status: true,
      }),
    );

    throw error;
  }
});

/**
 * A thunk to refresh sessions specifically for device details view.
 * @param {FetchParameters} data - The query parameters for the api call.
 * @returns {Promise<SessionAPIResponse<CommercialSessionInterface>>} The API response.
 */
export const refreshDeviceDetailsChargingSessions = createAsyncThunk<
  SessionAPIResponse<CommercialSessionInterface>,
  FetchParameters
>('device-details-charging-session/refreshDeviceDetailsChargingSessions', async (data: FetchParameters) => {
  const response = await paymentApi.fetchChargingSessions(removeEmptyKeys(data));
  return {
    ...response.data,
    results: response.data.results.map((roleData: any) => ({
      ...roleData,
      date: ConvertUTCtoDate(roleData.createdAt),
      id: roleData._id,
    })),
  };
});

/**
 * A thunk to fetch session filters list specifically for device details view.
 * @param {void} _ - Unused parameter.
 * @param {any} thunkAPI - The Redux Toolkit thunk API object.
 * @returns {Promise<CommonFilterProperties[]>} The list of filters formatted for UI display.
 */
export const fetchDeviceDetailsListSessionFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'device-details-charging-session/fetchDeviceDetailsListSessionFilters',
  async (_, { dispatch }) => {
    try {
      const response = await paymentApi.fetchChargingSessionsListFilters();

      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);

      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });

      throw error;
    }
  },
);

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Format a Date object to a YYYY-MM-DD string.
 * @param {Date} date - The date to format.
 * @returns {string} The formatted string.
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  ...(portalType === PortalType.NETWORKS
    ? [
      {
        name: 'createdDateRange',
        key: 'startDate_endDate',
        value: '',
        key1: 'startDate',
        value1: formatDate(yesterday),
        key2: 'endDate',
        value2: formatDate(today),
        type: filterType.DATE_RANGE,
        listOfValue: [],
        applyFilter: true,
        enable: true,
        disableClearFilter: true,
        disableClearFilterMessage: 'Date filter is required!',
        FEAddedFilter: true,
      } as CommonFilterProperties,
    ]
    : []),
];

const initialState: SessionInitialProperties<CommercialSessionInterface> = {
  payments: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
  TotalFinising: 0,
  totalInprocess: 0,
  totalterminated: 0,
};

export const deviceDetailsChargingSessionSlice = createSlice({
  name: 'device-details-charging-session',
  initialState,
  reducers: {
    /**
     * Action to update filters applied to device details charging sessions list.
     * @param {any} state - The current slice state.
     * @param {PayloadAction<FilterUpdateProperties>} action - The action payload containing filter updates.
     */
    updateDeviceDetailsSessionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Action to reset the isFilterUpdated status to false.
     * @param {any} state - The current slice state.
     */
    disableDeviceDetailsSessionFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Action to update sorting criteria for device details charging sessions.
     * @param {any} state - The current slice state.
     * @param {PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>} action - The action payload containing sort field and direction.
     */
    setDeviceDetailsSessionSortData: (
      state,
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>,
    ) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Configures extra reducers to handle async thunk lifecycle actions.
   * @param {any} builder - The builder object to assign action cases.
   */
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchDeviceDetailsChargingSessions.pending,
        (state: SessionInitialProperties<CommercialSessionInterface>) => {
          state.loading = true;
        },
      )
      .addCase(
        fetchDeviceDetailsChargingSessions.fulfilled,
        (
          state: SessionInitialProperties<CommercialSessionInterface>,
          action: { payload: SessionAPIResponse<CommercialSessionInterface> },
        ) => {
          state.loading = false;
          state.payments = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.TotalFinising = action.payload.TotalFinising;
          state.totalInprocess = action.payload.totalInprocess;
          state.totalterminated = action.payload.totalterminated;
        },
      )
      .addCase(
        fetchDeviceDetailsChargingSessions.rejected,
        (state: SessionInitialProperties<CommercialSessionInterface>) => {
          state.loading = false;
        },
      );
    builder.addCase(
      refreshDeviceDetailsChargingSessions.fulfilled,
      (
        state: SessionInitialProperties<CommercialSessionInterface>,
        action: { payload: SessionAPIResponse<CommercialSessionInterface> },
      ) => {
        state.payments = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.TotalFinising = action.payload.TotalFinising;
        state.totalInprocess = action.payload.totalInprocess;
        state.totalterminated = action.payload.totalterminated;
      },
    );
    builder
      .addCase(fetchDeviceDetailsListSessionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchDeviceDetailsListSessionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key),
          );
        },
      )
      .addCase(fetchDeviceDetailsListSessionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  updateDeviceDetailsSessionFilters,
  disableDeviceDetailsSessionFiltersApplied,
  setDeviceDetailsSessionSortData,
} = deviceDetailsChargingSessionSlice.actions;

export default deviceDetailsChargingSessionSlice.reducer;
